import { Controller } from "@hotwired/stimulus"
import { getString, setString } from "../utils/local_storage"

// Manages sidebar behavior for admin areas with Tailwind CSS
export default class extends Controller {
  static targets = ["sidebar", "content", "toggleIcon", "toggleText", "body"]

  connect() {
    console.log("Sidebar controller connected")

    // Check if sidebar state is stored in localStorage
    const sidebarCollapsed = getString("sidebarCollapsed") === "true"
    if (sidebarCollapsed) {
      this.collapse()
    }

    // Handle window resize events
    this.handleResponsiveLayout = this.handleResponsiveLayout.bind(this)
    window.addEventListener("resize", this.handleResponsiveLayout)

    // Initial check
    this.handleResponsiveLayout()
  }

  disconnect() {
    window.removeEventListener("resize", this.handleResponsiveLayout)
  }

  // Toggle sidebar expanded/collapsed state
  toggle(event) {
    if (event) event.preventDefault()

    if (this.isCollapsed()) {
      this.expand()
    } else {
      this.collapse()
    }
  }

  // Toggle sidebar on mobile devices
  toggleMobile(event) {
    if (event) event.preventDefault()
    if (window.innerWidth >= 768) return

    this.sidebarTarget.classList.toggle("tw-translate-x-0")
    this.sidebarTarget.classList.toggle("tw--translate-x-full")
  }

  // Collapse the sidebar
  collapse() {
    // Add collapsed class to sidebar
    this.sidebarTarget.classList.add("tw-sidebar-collapsed")

    // Add collapsed class to content
    this.contentTarget.classList.add("tw-admin-content-with-collapsed-sidebar")

    // Add collapsed class to body if available
    if (this.hasBodyTarget) {
      this.bodyTarget.classList.add("tw-sidebar-collapsed-body")
    }

    // Update toggle icon
    if (this.hasToggleIconTarget) {
      this.toggleIconTarget.classList.remove("fa-angle-left")
      this.toggleIconTarget.classList.add("fa-angle-right")
    }

    if (this.hasToggleTextTarget) {
      this.toggleTextTarget.textContent = "Expand"
    }

    // Store state
    setString("sidebarCollapsed", "true")

    // Close any open dropdown menus when sidebar collapses
    this.closeDropdowns()
  }

  // Expand the sidebar
  expand() {
    // Remove collapsed class from sidebar
    this.sidebarTarget.classList.remove("tw-sidebar-collapsed")

    // Remove collapsed class from content
    this.contentTarget.classList.remove("tw-admin-content-with-collapsed-sidebar")

    // Remove collapsed class from body if available
    if (this.hasBodyTarget) {
      this.bodyTarget.classList.remove("tw-sidebar-collapsed-body")
    }

    // Update toggle icon
    if (this.hasToggleIconTarget) {
      this.toggleIconTarget.classList.add("fa-angle-left")
      this.toggleIconTarget.classList.remove("fa-angle-right")
    }

    if (this.hasToggleTextTarget) {
      this.toggleTextTarget.textContent = "Collapse Menu"
    }

    // Store state
    setString("sidebarCollapsed", "false")
  }

  // Handle responsive layout changes
  handleResponsiveLayout() {
    if (window.innerWidth < 768) {
      // Mobile: Hide sidebar by default
      this.sidebarTarget.classList.add("tw-translate-x-[-100%]")
      this.sidebarTarget.classList.remove("tw-translate-x-0")
      this.contentTarget.classList.remove("tw-admin-content-with-collapsed-sidebar")

      // Add mobile class to body if available
      if (this.hasBodyTarget) {
        this.bodyTarget.classList.add("tw-mobile-sidebar")
      }
    } else {
      // Desktop: Show sidebar based on previous state
      this.sidebarTarget.classList.remove("tw-translate-x-[-100%]")
      this.sidebarTarget.classList.add("tw-translate-x-0")

      // Remove mobile class from body if available
      if (this.hasBodyTarget) {
        this.bodyTarget.classList.remove("tw-mobile-sidebar")
      }

      // Apply correct classes based on sidebar state
      if (this.isCollapsed()) {
        this.contentTarget.classList.add("tw-admin-content-with-collapsed-sidebar")
      } else {
        this.contentTarget.classList.remove("tw-admin-content-with-collapsed-sidebar")
      }
    }
  }

  // Helper to check if sidebar is currently collapsed
  isCollapsed() {
    return this.sidebarTarget.classList.contains("tw-sidebar-collapsed")
  }

  // Close all dropdown menus in the sidebar
  closeDropdowns() {
    if (!this.application) return

    // Find all sidebar dropdown controllers
    const dropdownControllers = this.application.controllers.filter(
      controller => controller.context.identifier === 'sidebar-dropdown'
    )

    // Hide all dropdown menus
    dropdownControllers.forEach(controller => {
      if (typeof controller.hide === 'function') {
        controller.hide()
      }
    })
  }
}